'use client';

import { useState, useEffect } from 'react';
import { useWebSocketVisitor } from '@/utils/websocket';
import PopupBanner from './PopupBanner';

export default function VisitorCounter({ page, disablePromotion = false }) {
  const [showCounter, setShowCounter] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState('home');

  // Determine the endpoint based on the page
  useEffect(() => {
    let endpoint = 'home';
    if (page.includes('booking') || page.includes('book-now')) {
      endpoint = 'rooms';
    } else if (page.includes('reservation')) {
      endpoint = 'reservations';
    } else if (page.includes('payment')) {
      endpoint = 'payments';
    } else if (page.includes('hotel')) {
      endpoint = 'hotels';
    }
    
    setCurrentEndpoint(endpoint);
  }, [page]);

  // Get visitor counts from WebSocket
  const { visitorCount, totalVisitors, connectionStatus, serverAvailable, reconnect } = useWebSocketVisitor(currentEndpoint);

  useEffect(() => {
    // Check if device is mobile
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-minimize on mobile
      if (window.innerWidth < 768) {
        setIsMinimized(true);
      }
    };

    // Check on initial load
    checkIfMobile();

    // Add resize listener
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleClose = () => {
    setShowCounter(false);
  };

  const toggleMinimize = () => {
    // On mobile, don't allow expanding if we're on mobile
    if (isMobile && isMinimized) {
      return;
    }
    setIsMinimized(!isMinimized);
  };

  // Don't show counter if not connected or if there are no visitors
  if (!showCounter || connectionStatus !== 'connected' || (visitorCount <= 0 && totalVisitors <= 0)) {
    return null;
  }

  return (
    <>
      <PopupBanner page={page} disablePromotion={disablePromotion} />
      <div className={`visitor-counter-wrapper fixed bottom-24 right-4 z-40 transition-all duration-300 ${isMinimized ? 'w-10' : 'w-auto'}`}>
        <div className="visitor-counter bg-white rounded-lg shadow-lg overflow-hidden border border-primary">
          {!isMinimized && (
            <div className="flex items-center p-2 bg-primary text-on-primary">
              <div className="visitor-icon mr-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
                  <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
                </svg>
              </div>
              <div className="visitor-text font-medium flex-grow">
                <div>
                  <span className="text-base">{visitorCount}</span>
                  <span className="ml-1 text-xs">{visitorCount === 1 ? ' person' : ' people'} viewing this page</span>
                </div>
                <div className="text-xs">
                  <span>{totalVisitors}</span>
                  <span className="ml-1">total online</span>
                </div>
              </div>
              <div className="flex ml-2">
                <button 
                  onClick={toggleMinimize} 
                  className="text-on-primary hover:text-on-primary focus:outline-none p-1 bg-primary"
                  aria-label="Minimize"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                  </svg>
                </button>
                <button 
                  onClick={handleClose} 
                  className="text-on-primary hover:text-on-primary focus:outline-none p-1 bg-primary"
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          {isMinimized && (
            <button 
              onClick={toggleMinimize}
              className="w-10 h-10 flex items-center justify-center bg-primary text-on-primary rounded-lg shadow-lg"
              aria-label="Expand visitor counter"
            >
              <div className="relative">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
                  <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
                </svg>
                <div className="absolute -top-1 -right-1 bg-white text-primary text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {totalVisitors > 99 ? '99+' : totalVisitors}
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
